"use client";

/**
 * Small circular icon button that reads the clipboard on click,
 * extracts a supported listing URL (bina.az / emlak.az / ev10.az /
 * arenda.az) if present, and hands it back to the parent. Surfaces
 * the outcome via a floating toast
 * anchored to the button - "No link found" would otherwise be
 * invisible when the icon is the only UI (previous version only
 * swapped the icon colour, which told users nothing).
 *
 * Intentional design choices:
 *   - Always a button (not auto-read on mount): browsers gate
 *     clipboard.readText() on user activation, and silent reads on
 *     iOS trigger a "pasted from clipboard" system banner.
 *   - Icon-only: stays small enough that the URL placeholder / typed
 *     text still reads without truncation on a narrow phone input.
 *   - Feedback as a floating toast, not an inline label-swap: a
 *     single emoji or icon is not a message; a full sentence is.
 *   - `labelOnPhone`: the word beside the icon under sm (the home hero,
 *     UX plan 3.1): on a phone the link comes from another app's share
 *     or copy, and a bare clipboard glyph was not read as "paste".
 */
import { useState } from "react";
import { Clipboard, Check, AlertTriangle } from "lucide-react";
import { extractListingUrl } from "@/lib/listing-url";
import { useT } from "@/lib/locale-context";

type Flash = { kind: "none" } | { kind: "ok" } | { kind: "miss" };

export default function PasteFromClipboard({
  onPaste,
  className = "",
  labelOnPhone = false,
}: {
  onPaste: (url: string) => void;
  className?: string;
  /** Under sm the button carries its word ("Yapışdırın") next to the
   *  icon; from sm up it stays the round icon. */
  labelOnPhone?: boolean;
}) {
  const t = useT();
  const [flash, setFlash] = useState<Flash>({ kind: "none" });

  const flashFor = (kind: Flash["kind"], ms: number) => {
    setFlash({ kind } as Flash);
    window.setTimeout(() => setFlash({ kind: "none" }), ms);
  };

  const handleClick = async () => {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      return flashFor("miss", 2000);
    }
    let text = "";
    try {
      text = await navigator.clipboard.readText();
    } catch {
      // Permission denied / unsupported - treat as miss, don't throw.
      return flashFor("miss", 2000);
    }
    const url = extractListingUrl(text);
    if (!url) return flashFor("miss", 2000);
    onPaste(url);
    flashFor("ok", 1200);
  };

  const isActive = flash.kind !== "none";
  const Icon =
    flash.kind === "ok" ? Check :
    flash.kind === "miss" ? AlertTriangle : Clipboard;
  const iconClass =
    flash.kind === "ok" ? "text-emerald-600" :
    flash.kind === "miss" ? "text-amber-600" : "text-gray-500";
  const ringClass =
    flash.kind === "ok" ? "bg-emerald-50 border-emerald-200" :
    flash.kind === "miss" ? "bg-amber-50 border-amber-200" :
    "bg-gray-100 hover:bg-gray-200 border-gray-200";

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        aria-label={t("paste.btn")}
        className={`flex h-8 items-center justify-center rounded-full border transition-colors ${ringClass} ${
          labelOnPhone ? "gap-1.5 pl-2 pr-2.5 text-xs font-semibold text-gray-700 sm:w-8 sm:gap-0 sm:p-0" : "w-8"
        }`}
        suppressHydrationWarning
      >
        <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} />
        {labelOnPhone && <span className="sm:hidden" aria-hidden>{t("paste.btn")}</span>}
      </button>

      {/* Floating toast - anchored to the button's right edge. Color
          is semantic (amber = miss, emerald = success) rather than a
          neutral dark chip, which keeps the toast visible against
          both light and dark page backgrounds. The previous
          bg-gray-900 blended into dark-themed pages, so the warning
          text was essentially invisible. */}
      {isActive && (
        <div
          role="status"
          className={`pointer-events-none absolute right-0 top-full z-20 mt-1.5 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-white shadow-lg animate-in fade-in slide-in-from-top-1 duration-150 ${
            flash.kind === "ok" ? "bg-emerald-600" : "bg-amber-600"
          }`}
        >
          {flash.kind === "ok" ? t("paste.ok") : t("paste.miss")}
          <span
            aria-hidden="true"
            className={`absolute -top-1 right-3 h-2 w-2 rotate-45 ${
              flash.kind === "ok" ? "bg-emerald-600" : "bg-amber-600"
            }`}
          />
        </div>
      )}
    </div>
  );
}
